import { useCallback, useEffect, useRef } from 'react';
import { Outlet } from 'react-router';

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  type ResizablePanelHandle,
} from '@accio3d/design-system/resizable';
import { useIsDesktop } from '@accio3d/design-system/hooks';
import { cn } from '@accio3d/design-system/utils';

import { AgentPanel } from '../agent/agent-panel';
import { useAgentSidebar } from '../../hooks/useAgentSidebar';
import { PAGE_SHELL } from './page-shell';
import { GlobalTopBar } from './global-top-bar';

export const AGENT_DEFAULT_SIZE = '380px';
export const AGENT_MIN_SIZE = '320px';
export const AGENT_MAX_SIZE = '600px';

interface AgentLayoutProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

function PageContent({ collapsed, onToggleCollapsed }: AgentLayoutProps) {
  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <GlobalTopBar collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />
      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className={PAGE_SHELL}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function AgentLayout({ collapsed, onToggleCollapsed }: AgentLayoutProps) {
  const { open: agentOpen, setOpen: setAgentOpen } = useAgentSidebar();
  const isDesktop = useIsDesktop();
  const agentPanel = useRef<ResizablePanelHandle | null>(null);

  /* Collapse the column rather than unmount it */
  useEffect(() => {
    if (!isDesktop) return;
    const handle = agentPanel.current;
    if (!handle) return;

    if (!agentOpen) {
      handle.collapse();
      return;
    }

    handle.expand();
    if (handle.getSize().inPixels < 1) handle.resize(AGENT_DEFAULT_SIZE);
  }, [agentOpen, isDesktop]);

  /* The column's width is the other half of "is the agent showing" */
  const onAgentResize = useCallback(
    (size: { inPixels: number }) => setAgentOpen(size.inPixels > 0),
    [setAgentOpen],
  );

  return isDesktop ? (
    <ResizablePanelGroup className="min-w-0 flex-1">
      <ResizablePanel
        id="agent"
        panelRef={agentPanel}
        collapsible
        collapsedSize="0px"
        defaultSize={agentOpen ? AGENT_DEFAULT_SIZE : '0px'}
        minSize={AGENT_MIN_SIZE}
        maxSize={AGENT_MAX_SIZE}
        onResize={onAgentResize}
      >
        <AgentPanel />
      </ResizablePanel>
      <ResizableHandle withHandle={agentOpen} className={cn(!agentOpen && 'hidden')} />
      <ResizablePanel id="content">
        <PageContent collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />
      </ResizablePanel>
    </ResizablePanelGroup>
  ) : (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      <AgentPanel />
      <div className="flex-1 overflow-hidden">
        <PageContent collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />
      </div>
    </div>
  );
}
